import React, { useState, useRef, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from 'react-i18next';
import JSZip from "jszip";

// Configuration from your provided values
const GENERATE_SAS_URL = "https://generate-sas-links.azurewebsites.net/api/GenerateSasLink";
const GENERATE_SAS_KEY = "8DLtaBtdoqXHUmB0XWLF9b4C89xitRcZSoyFaSO7bGyYAzFu5Ftz1A==";
const LOG_UPLOAD_URL = "https://upload-logger-fn.azurewebsites.net/api/LogUpload";
const LOG_UPLOAD_KEY = "eC1yyysMFu3EO4lip6a4J4Jgy-iE6LFXJ4mqNXJCngTsAzFup0VnYQ==";
const GET_UPLOADS_URL = "https://upload-logger-fn.azurewebsites.net/api/GetUserUploads";
const GET_UPLOADS_KEY = "Flmatv-3ZZrnTbdrWEhifd8WFz92pT8lUxS5ccFCYcTvAzFuQYq7xA==";

const UploadComponent = () => {
  const { accounts } = useMsal();
  const username = accounts[0]?.username || "";

  // State management
  const [files, setFiles] = useState([]);
  const [zipFileName, setZipFileName] = useState("");
  const [expiryDays, setExpiryDays] = useState(7);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(""); // New state for time estimate
  const [uploadHistory, setUploadHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fileInputRef = useRef();
  const { t } = useTranslation();

  const loadHistory = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(`${GET_UPLOADS_URL}?email=${encodeURIComponent(username)}`, {
        headers: { 'x-functions-key': GET_UPLOADS_KEY }
      });
      if (!res.ok) throw new Error(`History fetch failed: ${await res.text()}`);
      const data = await res.json();
      if (Array.isArray(data)) setUploadHistory(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (err) {
      console.error("Failed to fetch upload history", err);
    }
  }, [username]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).map(file => ({
      id: crypto.randomUUID(),
      fileObject: file,
    }));
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (idToRemove) => {
    setFiles(prevFiles => prevFiles.filter(item => item.id !== idToRemove));
  };

  // Helper function to format seconds into a readable time string
  const formatTime = (seconds) => {
    if (seconds === Infinity || isNaN(seconds)) return "";
    if (seconds > 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes} min ${remainingSeconds} sec remaining`;
    }
    return `${Math.round(seconds)} sec remaining`;
  };

  const handleUpload = async () => {
    if (files.length === 0) return alert(t('select_files_first_alert'));
    setIsUploading(true);
    setUploadProgress(0);
    setTimeRemaining("");
    setDownloadUrl("");

    try {
      const isZip = files.length > 1;
      let finalFileName;

      if (isZip) {
        let name = zipFileName.trim() ? zipFileName : `upload_${Date.now()}`;
        if (!name.toLowerCase().endsWith('.zip')) {
          name += '.zip';
        }
        finalFileName = name;
      } else {
        finalFileName = files[0].fileObject.name;
      }

      const sasResponse = await fetch(GENERATE_SAS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-functions-key': GENERATE_SAS_KEY },
        body: JSON.stringify({ fileName: finalFileName, expiryDays: expiryDays })
      });

      if (!sasResponse.ok) throw new Error(`Failed to get SAS token: ${await sasResponse.text()}`);
      const { sasUrl } = await sasResponse.json();

      let contentToUpload;
      if (isZip) {
        const zip = new JSZip();
        files.forEach(item => {
          zip.file(item.fileObject.name, item.fileObject);
        });
        contentToUpload = await zip.generateAsync({ type: "blob" });
      } else {
        contentToUpload = files[0].fileObject;
      }

      const xhr = new XMLHttpRequest();
      xhr.open("PUT", sasUrl, true);
      xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
      xhr.setRequestHeader("Content-Type", contentToUpload.type || "application/octet-stream");

      const startTime = Date.now(); // Record start time for speed calculation

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = (event.loaded / event.total) * 100;
          setUploadProgress(percent);

          const elapsedTime = (Date.now() - startTime) / 1000; // in seconds
          if (elapsedTime > 0) {
            const speed = event.loaded / elapsedTime; // bytes per second
            const remainingBytes = event.total - event.loaded;
            const remainingSeconds = remainingBytes / speed;
            setTimeRemaining(formatTime(remainingSeconds));
          }
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const logResponse = await fetch(LOG_UPLOAD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-functions-key': LOG_UPLOAD_KEY },
            body: JSON.stringify({ email: username, filename: finalFileName, url: sasUrl })
          });

          if (!logResponse.ok) {
            throw new Error(t('upload_failed_alert_log'));
          }

          setDownloadUrl(sasUrl);
          alert(t('upload_successful_alert'));
          setFiles([]);
          setZipFileName("");
          if (fileInputRef.current) fileInputRef.current.value = '';
          loadHistory();
        } else {
          throw new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`);
        }
        setIsUploading(false);
      };

      xhr.onerror = () => {
        setIsUploading(false);
        throw new Error(t('upload_error_alert', { fileName: finalFileName }));
      };

      xhr.send(contentToUpload);

    } catch (err) {
      setIsUploading(false);
      console.error("Upload process error:", err);
      alert(`${t('upload_error_alert_general', { error: err.message })}`);
    }
  };

  const handleCopyLink = () => {
    if (downloadUrl) {
      navigator.clipboard.writeText(downloadUrl)
        .then(() => alert(t('link_copied_alert')))
        .catch((err) => console.error("Failed to copy link: ", err));
    }
  };

  return (
    <div style={{ textAlign: "center", maxWidth: 500, margin: "2em auto" }}>
        <div style={{display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '1em'}}>
            <label htmlFor="file-input" style={{
                display: 'inline-flex', alignItems: 'center', border: '1px solid #ccc',
                borderRadius: '4px', padding: '6px 12px', cursor: 'pointer',
                backgroundColor: '#f0f0f0', color: '#333'
            }}>
                {t('choose_files_button')}
                <input id="file-input" ref={fileInputRef} type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} />
            </label>
        </div>

      {files.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, marginTop: '1em' }}>
          {files.map((item) => (
            <li key={item.id} style={{ marginBottom: "0.5em", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{item.fileObject.name}</span>
              <button onClick={() => removeFile(item.id)} style={{ marginLeft: "0.5em", border: "none", color: "#c00", cursor: "pointer", background: "none", fontSize: "1em" }}>
                ❌ {t('remove_file')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {files.length > 1 && (
        <div style={{ marginTop: "1em" }}>
          <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}>
            {t('zip_file_name_label', 'Zip File Name (optional):')}
          </label>
          <input 
            type="text" 
            value={zipFileName}
            onChange={(e) => setZipFileName(e.target.value)}
            placeholder="e.g., project-files"
            style={{ padding: "0.5em", width: "80%", borderRadius: "6px", border: "1px solid #ccc" }}
          />
        </div>
      )}

      <div style={{ marginTop: "1em" }}>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5em" }}>
          {t('link_expiration_label')}
        </label>
        <input type="number" value={expiryDays} min={1} max={30} onChange={(e) => setExpiryDays(Math.min(30, Math.max(1, parseInt(e.target.value) || 1)))} style={{ padding: "0.5em", width: "120px", borderRadius: "6px", border: "1px solid #ccc", textAlign: "center" }}/>
      </div>

      <button onClick={handleUpload} disabled={isUploading || files.length === 0} style={{
          marginTop: "1em", padding: "0.6em 1.5em", backgroundColor: "#28a745",
          color: "#fff", border: "none", borderRadius: "8px", fontSize: "1em",
          fontWeight: "bold", cursor: "pointer"
      }}>
        {isUploading ? t('uploading_button') : t('upload_button')}
      </button>

      {isUploading && (
        <div style={{ marginTop: '1em' }}>
            <div style={{ width: '100%', backgroundColor: '#e0e0e0', borderRadius: '8px', position: 'relative', height: '24px' }}>
              <div style={{
                  width: `${uploadProgress}%`, height: '100%', backgroundColor: '#4CAF50',
                  borderRadius: '8px', transition: 'width 0.1s ease-in-out'
              }} />
              <div style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  color: '#fff', textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}>
                {`${Math.round(uploadProgress)}%`}
              </div>
            </div>
            <div style={{ marginTop: '0.5em', fontSize: '0.9em', color: '#555' }}>
                {timeRemaining}
            </div>
        </div>
      )}

      {downloadUrl && (
        <div style={{ marginTop: "1.5em" }}>
          <p style={{ fontWeight: "bold" }}>{t('download_link_label')}</p>
          <a href={downloadUrl} target="_blank" rel="noreferrer" style={{ wordBreak: "break-all", display: "block", fontSize: "0.9em", color: "#0077cc", marginBottom: "0.5em" }}>
            {downloadUrl}
          </a>
          <button onClick={handleCopyLink} style={{
              backgroundColor: "#0077cc", color: "white", border: "none",
              padding: "0.5em 1em", borderRadius: "6px", cursor: "pointer"
          }}>
            {t('copy_link_button')}
          </button>
        </div>
      )}

      <div style={{ marginTop: "2em" }}>
        <button onClick={() => setShowHistory(!showHistory)} style={{
            backgroundColor: "#555", color: "white", border: "none",
            padding: "0.5em 1em", borderRadius: "6px", cursor: "pointer"
        }}>
          {showHistory ? t('hide_history_button') : t('show_history_button')}
        </button>
      </div>

      {showHistory && uploadHistory.length > 0 && (
        <div style={{ backgroundColor: "#f9f9f9", borderRadius: "16px", padding: "1em", marginTop: "2em", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}>
          <h3>{t('upload_history_title')}</h3>
          <ul style={{ padding: 0, textAlign: "left" }}>
            {uploadHistory.map((entry, i) => (
              <li key={i} style={{ marginBottom: "1em" }}>
                <a href={entry.url} target="_blank" rel="noreferrer" style={{ wordBreak: "break-word", color: "#0077cc" }}>
                  {entry.filename}
                </a>
                <div style={{ fontSize: "0.8em", color: "#666" }}>
                  {t('uploaded_on', { date: new Date(entry.date).toLocaleString() })}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadComponent;
